import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Share2, Zap, ShieldAlert, CheckCircle, AlertTriangle, Clock, Play, Globe, Eye, EyeOff, GitFork, GitBranch, Edit3, ChevronDown, Search, Trophy, Github, Image as ImageIcon } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import { isValidModelId, getInvalidModelVoices, getModelValidationMessage } from '../lib/modelValidation';
import { ClipFromTranscript } from '../components/ClipButton';
import SaveToGitHub from '../components/SaveToGitHub';
import ProvenanceView from '../components/enterprise/ProvenanceView';
import ProjectVersionsView from '../components/ProjectVersionsView';

const VERDICT_COLORS = { Pass: '#22C55E', Conditional: '#F59E0B', Fail: '#EF4444' };
const SEVERITY_COLORS = { low: '#22C55E', medium: '#F59E0B', high: '#EF4444', critical: '#EF4444' };
const TIER_COLORS = { free: '#A0A0A0', economy: '#22C55E', standard: '#F59E0B', premium: '#EF4444' };

// #655: Form for answering a single open question.
// Candidate answers render as radios; "Other" reveals a textarea.
function OQForm({ oq, qaAnswers, setQaAnswers, onSubmit, submitting, accentColor }) {
  const answer = qaAnswers[oq.id] || {};
  const candidates = oq.candidate_answers || [];
  const setAnswerField = (field, value) =>
    setQaAnswers(prev => ({ ...prev, [oq.id]: { ...(prev[oq.id] || {}), [field]: value } }));
  const chosen = answer.choice;
  const showTextarea = !chosen || chosen === 'other' || candidates.length === 0;
  const canSubmit = chosen === 'other' || !chosen
    ? !!(answer.text || '').trim()
    : true;
  return (
    <div className="p-4 bg-[#1A1A1A]" style={{ borderLeft: `4px solid ${accentColor}` }} data-testid={`oq-${oq.oq_number}`}>
      <div className="flex items-start gap-3">
        <span className="text-xs font-mono text-[#A0A0A0] whitespace-nowrap">Q{oq.oq_number}</span>
        <div className="flex-1">
          <div className="text-sm text-[#F5F5F5]">{oq.question}</div>
          {(oq.raised_by_voice || oq.raised_in_phase != null) && (
            <div className="text-[11px] font-mono text-[#A0A0A0] mt-1">
              {oq.raised_by_voice ? `Raised by ${oq.raised_by_voice}` : 'Pre-flight'}
              {oq.raised_in_phase != null ? `, phase ${oq.raised_in_phase + 1}` : ''}
            </div>
          )}
          {oq.rationale && (
            <div className="text-xs text-[#A0A0A0] mt-2 italic">{oq.rationale}</div>
          )}
        </div>
      </div>

      {candidates.length > 0 && (
        <div className="mt-4 space-y-2" role="radiogroup" aria-label={`Answers for Q${oq.oq_number}`}>
          {candidates.map(c => (
            <label key={c.id} className="flex items-start gap-2 cursor-pointer min-h-[44px] py-1">
              <input
                type="radio"
                name={`oq-${oq.id}`}
                value={c.id}
                checked={chosen === c.id}
                onChange={() => setAnswerField('choice', c.id)}
                className="mt-1"
                data-testid={`oq-${oq.oq_number}-candidate-${c.id}`}
              />
              <span className="text-sm text-[#F5F5F5]">{c.label}</span>
            </label>
          ))}
          <label className="flex items-start gap-2 cursor-pointer min-h-[44px] py-1">
            <input
              type="radio"
              name={`oq-${oq.id}`}
              value="other"
              checked={chosen === 'other'}
              onChange={() => setAnswerField('choice', 'other')}
              className="mt-1"
              data-testid={`oq-${oq.oq_number}-other`}
            />
            <span className="text-sm text-[#F5F5F5]">Other (type below)</span>
          </label>
        </div>
      )}

      {showTextarea && (
        <textarea
          value={answer.text || ''}
          onChange={(e) => setAnswerField('text', e.target.value)}
          placeholder="Type your answer…"
          rows={3}
          className="mt-3 w-full p-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:border-[#FF4D00] focus:outline-none font-mono"
          data-testid={`oq-${oq.oq_number}-textarea`}
        />
      )}

      <div className="flex items-center gap-3 mt-3">
        <label className="text-xs font-mono text-[#A0A0A0]">
          State after:
          <select
            value={answer.state_after || 'resolved'}
            onChange={(e) => setAnswerField('state_after', e.target.value)}
            className="ml-2 bg-[#0F0F0F] border border-[#2A2A2A] text-[#F5F5F5] text-xs font-mono p-1"
            data-testid={`oq-${oq.oq_number}-state-after`}
          >
            <option value="resolved">resolved</option>
            <option value="direction_given">direction given</option>
            <option value="placement_decided">placement decided</option>
            <option value="partially_resolved">partially resolved</option>
          </select>
        </label>
        <button
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          className="ml-auto px-4 py-2 min-h-[44px] text-sm font-medium disabled:opacity-40"
          style={{ backgroundColor: accentColor, color: '#000' }}
          data-testid={`oq-${oq.oq_number}-submit`}
        >
          {submitting ? 'Submitting…' : 'Submit answer'}
        </button>
      </div>
    </div>
  );
}

export default function Results() {
  const { frayId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fray, setFray] = useState(null);
  const [tab, setTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [lineage, setLineage] = useState(null);
  const [showSaveToGitHub, setShowSaveToGitHub] = useState(false);
  // #655 B2: Q&A + Decisions state
  const [qaData, setQaData] = useState(null);
  const [decisionsData, setDecisionsData] = useState(null);
  // #1031: per-phase secretary summaries
  const [phaseSummariesData, setPhaseSummariesData] = useState(null);
  const [qaSubmitting, setQaSubmitting] = useState(null);  // oq_id of in-flight submission
  const [qaAnswers, setQaAnswers] = useState({});  // { [oq_id]: { choice: candidate_id|'other', text: str } }
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    API.get(`/api/frays/${frayId}`)
      .then(r => setFray(r.data))
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
    API.get(`/api/frays/${frayId}/lineage`).then(r => setLineage(r.data)).catch(() => {});
  }, [frayId, navigate]);

  // #655: fetch QA register + decisions; poll on pause/running (3s / 5s)
  useEffect(() => {
    const loadQA = () => {
      API.get(`/api/frays/${frayId}/qa/open-questions`)
        .then(r => setQaData(r.data))
        .catch(() => {});
      API.get(`/api/frays/${frayId}/qa/decisions`)
        .then(r => setDecisionsData(r.data))
        .catch(() => {});
      // #1031: secretary summaries — non-fatal on 404 (older frays)
      API.get(`/api/frays/${frayId}/phase-summaries`)
        .then(r => setPhaseSummariesData(r.data))
        .catch(() => {});
    };
    loadQA();
    const status = fray?.status;
    if (status === 'paused') {
      const id = setInterval(loadQA, 3000);
      return () => clearInterval(id);
    }
    if (status === 'running' || status === 'pending' || status === 'created') {
      const id = setInterval(loadQA, 5000);
      return () => clearInterval(id);
    }
  }, [frayId, fray?.status]);

  useEffect(() => {
    if (fray?.debate_mode === 'cat_fight') setTab('fight-card');
  }, [fray?.debate_mode]);

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/share/${fray?.share_token}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportMarkdown = async () => {
    try {
      const res = await API.get(`/api/frays/${frayId}/export/markdown`);
      const blob = new Blob([res.data.markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fray-${fray?.template_slug || 'custom'}-${frayId.slice(0, 8)}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleExportPDF = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('fray_token');
      const res = await fetch(`${backendUrl}/api/frays/${frayId}/export/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('PDF export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fray-${fray?.template_slug || 'custom'}-${frayId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  const handleRerun = async () => {
    try {
      const res = await API.post(`/api/frays/${frayId}/rerun`);
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      console.error('Re-run failed:', err);
    }
  };

  // #655: Q&A handlers
  const handleAnswerOQ = async (oq) => {
    const answer = qaAnswers[oq.id] || {};
    const choice = answer.choice;
    const text = (answer.text || '').trim();
    const stateAfter = answer.state_after || 'resolved';
    let disclosureBody = '';
    if (choice === 'other' || !choice) {
      if (!text) return;  // guard: empty submission
      disclosureBody = text;
    } else {
      const match = (oq.candidate_answers || []).find(c => c.id === choice);
      disclosureBody = match ? match.body : text;
    }
    setQaSubmitting(oq.id);
    try {
      await API.post(`/api/frays/${frayId}/qa/open-questions/${oq.id}/disclosures`, {
        disclosure_markdown: disclosureBody,
        state_after: stateAfter,
      });
      const r = await API.get(`/api/frays/${frayId}/qa/open-questions`);
      setQaData(r.data);
      setQaAnswers(prev => { const n = { ...prev }; delete n[oq.id]; return n; });
    } catch (err) {
      console.error('Answer submission failed:', err);
    } finally {
      setQaSubmitting(null);
    }
  };

  const handleResumeFray = async () => {
    setResuming(true);
    try {
      const res = await API.post(`/api/frays/${frayId}/qa/resume`, {});
      if (res.data?.status === 'resumed') {
        setTab('transcript');
        // Refresh fray state + OQ data
        const fr = await API.get(`/api/frays/${frayId}`);
        setFray(fr.data);
        const qr = await API.get(`/api/frays/${frayId}/qa/open-questions`);
        setQaData(qr.data);
      } else if (res.data?.status === 'blocked') {
        // stale state — refetch OQs so the UI shows the remaining blockers
        const qr = await API.get(`/api/frays/${frayId}/qa/open-questions`);
        setQaData(qr.data);
      } else if (res.data?.status === 'not_paused') {
        // Runner already moved on; refresh to pick up latest state
        const fr = await API.get(`/api/frays/${frayId}`);
        setFray(fr.data);
      }
    } catch (err) {
      console.error('Resume failed:', err);
    } finally {
      setResuming(false);
    }
  };

  const handleFork = async () => {
    try {
      const res = await API.post(`/api/frays/${frayId}/fork`);
      // Navigate to the custom fray page to let them modify before running
      navigate(`/fray/${res.data.id}/results`);
      // Reload to show the forked version
      window.location.reload();
    } catch (err) {
      console.error('Fork failed:', err);
    }
  };

  const handleTogglePublic = async () => {
    const newVisibility = fray?.visibility_scope === 'public' ? 'private' : 'public';
    try {
      await API.put(`/api/frays/${frayId}/visibility`, { visibility: newVisibility });
      setFray(prev => ({ ...prev, visibility_scope: newVisibility }));
    } catch (err) {
      console.error('Visibility toggle failed:', err);
    }
  };

  // Load usage data — MUST be before any early returns (React hooks rules)
  const [usageData, setUsageData] = useState(null);
  useEffect(() => {
    if (frayId) {
      API.get(`/api/frays/${frayId}/usage`).then(r => setUsageData(r.data)).catch(() => {});
    }
  }, [frayId]);

  if (loading || !fray) return (
    <div className="min-h-screen bg-[#0F0F0F]"><Header /><div className="pt-24 flex justify-center"><div className="w-6 h-6 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" /></div></div>
  );

  const verdict = fray.verdict || {};
  const transcript = fray.transcript || [];

  // Handle stopped/failed/interrupted Frays with no content
  // #978: `interrupted` joined the terminal-status set (runner
  // CancelledError path); render the same no-content screen.
  const hasContent = transcript.length > 0 || verdict.verdict;
  if (!hasContent && (
    fray.status === 'stopped'
    || fray.status === 'failed'
    || fray.status === 'interrupted'
    || fray.status === 'created'
  )) {
    return <StoppedFrayScreen fray={fray} frayId={frayId} onRerun={handleRerun} />;
  }

  const isCatFight = fray.debate_mode === 'cat_fight';
  // #655: conditionally include 'questions' and 'decisions' when the fray has OQs / decisions
  const hasOQs = (qaData?.open_questions?.length || 0) > 0;
  const hasDecisions = (decisionsData?.decisions?.length || 0) > 0;
  const tabs = isCatFight
    ? [
        'fight-card',
        ...(hasOQs ? ['questions'] : []),
        'highlights',
        ...(hasDecisions ? ['decisions'] : []),
        'transcript',
        'usage',
        'provenance',
      ]
    : [
        'summary',
        ...(hasOQs ? ['questions'] : []),
        'risks',
        'recommendations',
        ...(hasDecisions ? ['decisions'] : []),
        'transcript',
        'usage',
        'provenance',
      ];

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-4xl mx-auto">
        <Link to="/dashboard" className="flex items-center gap-1 text-xs text-[#A0A0A0] hover:text-[#FF4D00] font-mono mb-4 min-h-[44px]" data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Dashboard
        </Link>

        {/* Verdict Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.42 }}
          className="p-4 md:p-8 bg-[#1A1A1A] border-2 mb-6 md:mb-8"
          style={{ borderColor: VERDICT_COLORS[verdict.verdict] || '#FF4D00' }}
          data-testid="verdict-badge"
        >
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <span className="text-xs font-mono text-[#A0A0A0] block mb-1">FINAL CALL</span>
              <h1 className="text-3xl md:text-5xl font-bold font-['Barlow_Condensed'] mb-2"
                style={{ color: VERDICT_COLORS[verdict.verdict] || '#FF4D00' }}>
                {verdict.verdict || 'Pending'}
              </h1>
              <span className="text-xs font-mono text-[#A0A0A0]">
                Confidence: {verdict.verdict_confidence || 'N/A'}
              </span>
            </div>
            <div className="sm:text-right">
              <span className="text-xs font-mono text-[#A0A0A0] block">{fray.template_name}</span>
              {fray.tension_score > 0 && (
                <div className="flex items-center gap-1 mt-1 justify-end">
                  <Zap className="w-3 h-3 text-[#FF4D00]" />
                  <span className="text-sm font-mono text-[#FF4D00]">{fray.tension_score}% friction</span>
                </div>
              )}
              {fray.duration_seconds > 0 && (
                <div className="flex items-center gap-1 mt-1 justify-end">
                  <Clock className="w-3 h-3 text-[#A0A0A0]" />
                  <span className="text-xs font-mono text-[#A0A0A0]">{Math.round(fray.duration_seconds / 60)}m {fray.duration_seconds % 60}s</span>
                </div>
              )}
            </div>
          </div>
          <p className="text-[#A0A0A0] text-sm mt-4 leading-relaxed">{verdict.executive_summary}</p>

          {verdict.hot_take_quote && (
            <div className="mt-4 p-3 bg-[#FF4D00]/5 border border-[#FF4D00]/20">
              <Zap className="w-3 h-3 text-[#FF4D00] inline mr-1" />
              <span className="text-xs font-mono text-[#FF4D00]">HOT TAKE:</span>
              <p className="text-sm text-[#F5F5F5] mt-1 italic">"{verdict.hot_take_quote}"</p>
            </div>
          )}
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#2A2A2A] overflow-x-auto" data-testid="result-tabs">
          {tabs.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 md:px-4 py-2 min-h-[44px] text-xs font-mono capitalize transition-colors border-b-2 -mb-px whitespace-nowrap ${
                tab === t ? 'text-[#FF4D00] border-[#FF4D00]' : 'text-[#A0A0A0] border-transparent hover:text-[#F5F5F5]'
              }`}
              data-testid={`tab-${t}`}
            >{t}</button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'summary' && (
          <div data-testid="summary-tab">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">Executive Summary</h2>
            <p className="text-[#A0A0A0] leading-relaxed mb-6">{verdict.executive_summary}</p>

            <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-3">Scenario</h3>
            <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
              <p className="text-sm text-[#F5F5F5]">{fray.scenario}</p>
            </div>

            {verdict.dissent_record?.length > 0 && (
              <>
                <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-3">Dissent Record</h3>
                <div className="space-y-2">
                  {verdict.dissent_record.map((d, i) => (
                    <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center gap-3">
                      <span className="text-xs font-mono text-[#FF4D00]">{d.voice}</span>
                      <span className="text-xs text-[#A0A0A0]">{d.position}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'risks' && (
          <div data-testid="risks-tab">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">Risk Register</h2>
            {verdict.risk_register?.length > 0 ? (
              <div className="space-y-2">
                {verdict.risk_register.map((risk, i) => (
                  <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] flex items-start gap-3">
                    <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" style={{ color: SEVERITY_COLORS[risk.severity] || '#F59E0B' }} />
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">{risk.risk}</span>
                        <span className="text-[9px] px-1.5 py-0.5 border font-mono capitalize"
                          style={{ color: SEVERITY_COLORS[risk.severity], borderColor: SEVERITY_COLORS[risk.severity] + '40' }}>
                          {risk.severity}
                        </span>
                      </div>
                      <span className="text-xs text-[#A0A0A0] font-mono">Flagged by: {risk.voice}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#A0A0A0] text-sm">No significant risks identified.</p>
            )}
          </div>
        )}

        {tab === 'recommendations' && (
          <div data-testid="recommendations-tab">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">Recommendations</h2>
            {verdict.recommendations?.length > 0 ? (
              <div className="space-y-2">
                {verdict.recommendations.map((rec, i) => (
                  <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-[#22C55E] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm">{rec.text}</p>
                      <span className="text-xs text-[#A0A0A0] font-mono mt-1 block">From: {rec.voice}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#A0A0A0] text-sm">No specific recommendations extracted.</p>
            )}
          </div>
        )}

        {tab === 'transcript' && (
          <div data-testid="transcript-tab">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">Full Transcript</h2>
            {transcript.length > 0 ? (
              <div className="space-y-4">
                {transcript.map((entry, i) => {
                  const voice = fray.voices?.find(v => v.name === entry.voice);
                  // #974: FOTD historically wrote `text`, fray_runner writes
                  // `content`. Tolerate both so legacy FOTD rows still render.
                  const rawContent = entry.content ?? entry.text ?? '';
                  // #36: Parse render_specs from Art Director messages
                  const specsMatch = rawContent.match(/<render_specs>([\s\S]*?)<\/render_specs>/);
                  let specs = null;
                  let displayContent = rawContent;
                  if (specsMatch) {
                    try { specs = JSON.parse(specsMatch[1].trim()); } catch {}
                    displayContent = rawContent.replace(/<render_specs>[\s\S]*?<\/render_specs>/, '').trim();
                  }
                  return (
                    <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <div className="w-5 h-5 flex items-center justify-center border text-[8px] font-bold shrink-0"
                          style={{ borderColor: voice?.color || '#A0A0A0', color: voice?.color || '#A0A0A0' }}>
                          {entry.voice?.charAt(0)}
                        </div>
                        <span className="text-xs font-medium font-['Barlow_Condensed']" style={{ color: voice?.color }}>{entry.voice}</span>
                        <span className="text-[10px] font-mono text-[#A0A0A0] sm:ml-auto">Phase {entry.phase}: {entry.phase_name}</span>
                        <ClipFromTranscript frayId={frayId} voice={entry.voice} phase={entry.phase} text={rawContent.slice(0, 500)} />
                      </div>
                      <p className="text-sm text-[#F5F5F5] leading-relaxed whitespace-pre-wrap">{displayContent}</p>
                      {/* #36: Render specs display in transcript */}
                      {specs && Array.isArray(specs) && specs.length > 0 && (
                        <div className="mt-3 p-2 bg-[#3B82F6]/5 border border-[#3B82F6]/20" data-testid={`render-specs-transcript-${i}`}>
                          <span className="text-[9px] font-mono text-[#3B82F6] block mb-1.5 flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" /> RENDER SPECS ({specs.length})
                          </span>
                          <div className="space-y-1">
                            {specs.map((spec, si) => (
                              <div key={si} className="flex items-start gap-2 text-[10px]">
                                <span className="font-mono text-[#3B82F6] shrink-0">{spec.spec_id || `#${si+1}`}</span>
                                <span className="text-[#A0A0A0] flex-1">{spec.brief}</span>
                                {spec.aspect_ratio && <span className="font-mono text-[#A0A0A0] shrink-0">{spec.aspect_ratio}</span>}
                                {spec.type && <span className="font-mono text-[#A0A0A0] shrink-0">{spec.type}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[#A0A0A0] text-sm">Transcript not available.</p>
            )}
          </div>
        )}

        {tab === 'fight-card' && (
          <div data-testid="fight-card-tab">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">Fight Card</h2>
            {fray.fight_card ? (
              <>
                {/* Winner announcement */}
                <div className="p-6 bg-[#EF4444]/5 border border-[#EF4444]/30 text-center mb-6">
                  <span className="text-[10px] font-mono text-[#EF4444] block mb-1">
                    {fray.fight_card.decision === 'knockout' ? 'KNOCKOUT' : fray.fight_card.decision === 'unanimous' ? 'UNANIMOUS DECISION' : fray.fight_card.decision === 'split_decision' ? 'SPLIT DECISION' : 'DRAW'}
                  </span>
                  <span className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed'] text-[#F5F5F5]">
                    {fray.fight_card.winner || 'Draw'}
                  </span>
                  <p className="text-sm text-[#A0A0A0] mt-2">{fray.fight_card.rounds_completed} rounds · {fray.fight_card.total_heckles || 0} heckles</p>
                </div>

                {/* Fight summary */}
                {fray.fight_card.fight_summary && (
                  <p className="text-[#A0A0A0] leading-relaxed mb-6">{fray.fight_card.fight_summary}</p>
                )}

                {/* Voice scores */}
                {fray.fight_card.voice_scores && (
                  <div className="space-y-3 mb-6">
                    {fray.fight_card.voice_scores.map((vs, i) => (
                      <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-3">
                          <span className="text-sm font-semibold font-['Barlow_Condensed']">{vs.voice}</span>
                          <span className="text-xs font-mono text-[#A0A0A0]">
                            AGG:{vs.aggression} DEF:{vs.defense} CROWD:{vs.crowd_work}
                          </span>
                        </div>
                        <div className="flex gap-2 mb-2">
                          {['aggression', 'defense', 'crowd_work'].map(stat => (
                            <div key={stat} className="flex-1">
                              <div className="text-[8px] font-mono text-[#A0A0A0] mb-1 capitalize">{stat.replace('_', ' ')}</div>
                              <div className="h-2 bg-[#2A2A2A] overflow-hidden">
                                <div className="h-full bg-[#EF4444]" style={{ width: `${(vs[stat] || 0) * 10}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                        {vs.best_blow && <p className="text-[10px] text-[#22C55E] font-mono mt-2">Best blow: {vs.best_blow}</p>}
                        {vs.weakest_moment && <p className="text-[10px] text-[#EF4444] font-mono">Weak moment: {vs.weakest_moment}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* MVP Heckle */}
                {fray.fight_card.mvp_heckle && (
                  <div className="p-4 bg-[#F59E0B]/5 border border-[#F59E0B]/30">
                    <span className="text-[10px] font-mono text-[#F59E0B] block mb-1">MVP HECKLE</span>
                    <p className="text-sm">"{fray.fight_card.mvp_heckle.text}"</p>
                    <p className="text-xs text-[#A0A0A0] mt-1">— {fray.fight_card.mvp_heckle.author}</p>
                    {fray.fight_card.mvp_heckle.impact && <p className="text-[10px] text-[#F59E0B] mt-1">{fray.fight_card.mvp_heckle.impact}</p>}
                  </div>
                )}
              </>
            ) : (
              <p className="text-[#A0A0A0] text-sm">Fight card not available.</p>
            )}
          </div>
        )}

        {tab === 'highlights' && (
          <div data-testid="highlights-tab">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">Highlight Reel</h2>
            {fray.highlight_reel?.highlights?.length > 0 ? (
              <div className="space-y-4">
                {fray.highlight_reel.highlights.map((h, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                    className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold font-['Barlow_Condensed'] text-[#EF4444]">#{h.rank}</span>
                        <span className="text-sm font-semibold font-['Barlow_Condensed']">{h.title}</span>
                      </div>
                      <span className="text-[9px] font-mono text-[#A0A0A0] shrink-0">{h.voice} · Round {h.round}</span>
                    </div>
                    <blockquote className="border-l-2 border-[#EF4444] pl-3 text-sm text-[#F5F5F5] leading-relaxed mb-2">
                      {h.quote}
                    </blockquote>
                    <p className="text-[10px] text-[#A0A0A0] font-mono">{h.why}</p>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-[#A0A0A0] text-sm">No highlights extracted.</p>
            )}
          </div>
        )}

        {tab === 'usage' && (
          <div data-testid="usage-tab">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">Usage & Cost</h2>
            {usageData?.totals?.total_tokens > 0 ? (
              <>
                {/* Summary */}
                <div className="p-4 bg-[#FF4D00]/5 border border-[#FF4D00]/20 mb-6">
                  <span className="text-sm">This Fray used <strong>{usageData.totals.total_tokens?.toLocaleString()}</strong> tokens and cost <strong>${usageData.totals.total_cost_usd?.toFixed(4)}</strong> on your key in <strong>{Math.round((usageData.totals.total_time_ms || 0) / 1000)}s</strong></span>
                </div>

                {/* Per-voice table */}
                <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Per Voice</h3>
                <div className="border border-[#2A2A2A] mb-6 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#1A1A1A]">
                      <tr>
                        <th className="text-left p-2 font-mono text-[#A0A0A0]">Voice</th>
                        <th className="text-left p-2 font-mono text-[#A0A0A0]">Model</th>
                        <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens In</th>
                        <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens Out</th>
                        <th className="text-right p-2 font-mono text-[#A0A0A0]">Cost</th>
                        <th className="text-right p-2 font-mono text-[#A0A0A0]">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageData.voice_usage?.map((vu, i) => (
                        <tr key={i} className="border-t border-[#2A2A2A]">
                          <td className="p-2">{vu.voice_name} <span className="text-[#A0A0A0]">R{vu.round_number}</span></td>
                          <td className="p-2 font-mono text-[#A0A0A0]">{vu.model}</td>
                          <td className="p-2 text-right font-mono">{vu.tokens_prompt?.toLocaleString()}</td>
                          <td className="p-2 text-right font-mono">{vu.tokens_completion?.toLocaleString()}</td>
                          <td className="p-2 text-right font-mono text-[#22C55E]">${(vu.cost_usd || 0).toFixed(4)}</td>
                          <td className="p-2 text-right font-mono text-[#A0A0A0]">{((vu.generation_time_ms || 0) / 1000).toFixed(1)}s</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Per-round table */}
                {usageData.round_summaries?.length > 0 && (
                  <>
                    <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Per Round</h3>
                    <div className="border border-[#2A2A2A] mb-6 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-[#1A1A1A]">
                          <tr>
                            <th className="text-left p-2 font-mono text-[#A0A0A0]">Round</th>
                            <th className="text-right p-2 font-mono text-[#A0A0A0]">Voices</th>
                            <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens In</th>
                            <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens Out</th>
                            <th className="text-right p-2 font-mono text-[#A0A0A0]">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usageData.round_summaries.map((rs, i) => (
                            <tr key={i} className="border-t border-[#2A2A2A]">
                              <td className="p-2 font-mono">Round {rs.round_number}</td>
                              <td className="p-2 text-right">{rs.voices_completed}</td>
                              <td className="p-2 text-right font-mono">{rs.total_tokens_in?.toLocaleString()}</td>
                              <td className="p-2 text-right font-mono">{rs.total_tokens_out?.toLocaleString()}</td>
                              <td className="p-2 text-right font-mono text-[#22C55E]">${(rs.total_cost_usd || 0).toFixed(4)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Model breakdown */}
                {usageData.totals.model_breakdown && (
                  <>
                    <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">By Model</h3>
                    <div className="space-y-2 mb-6">
                      {Object.entries(usageData.totals.model_breakdown).map(([model, stats]) => (
                        <div key={model} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                          <div>
                            <span className="text-xs font-mono">{model}</span>
                            <span className="text-[9px] text-[#A0A0A0] ml-2">{stats.turns} turn{stats.turns !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs font-mono">{stats.tokens?.toLocaleString()} tokens</span>
                            <span className="text-xs font-mono text-[#22C55E] ml-3">${stats.cost_usd?.toFixed(4)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* #42: Renderer cost breakdown */}
                {usageData.renderer_costs && usageData.renderer_costs.total_images > 0 && (
                  <>
                    <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-[#3B82F6]" /> Renderer Costs
                    </h3>
                    <div className="p-4 bg-[#3B82F6]/5 border border-[#3B82F6]/20 mb-4" data-testid="renderer-costs-summary">
                      <div className="flex flex-wrap gap-4 md:gap-6 text-xs">
                        <div>
                          <span className="text-[#A0A0A0] font-mono block text-[9px]">IMAGES</span>
                          <span className="font-mono text-[#3B82F6]">{usageData.renderer_costs.total_images}</span>
                        </div>
                        <div>
                          <span className="text-[#A0A0A0] font-mono block text-[9px]">TOKENS</span>
                          <span className="font-mono">{(usageData.renderer_costs.total_tokens || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="border border-[#2A2A2A] mb-6 overflow-x-auto" data-testid="renderer-costs-breakdown">
                      <table className="w-full text-xs">
                        <thead className="bg-[#1A1A1A]">
                          <tr>
                            <th className="text-left p-2 font-mono text-[#A0A0A0]">Phase</th>
                            <th className="text-right p-2 font-mono text-[#A0A0A0]">Images</th>
                            <th className="text-right p-2 font-mono text-[#A0A0A0]">Failed</th>
                            <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens In</th>
                            <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens Out</th>
                            <th className="text-left p-2 font-mono text-[#A0A0A0]">Model</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(usageData.renderer_costs)
                            .filter(([k]) => k.startsWith('phase_'))
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([phase, data]) => (
                              <tr key={phase} className="border-t border-[#2A2A2A]">
                                <td className="p-2 font-mono">{phase.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                                <td className="p-2 text-right font-mono text-[#3B82F6]">{data.images_generated || 0}</td>
                                <td className="p-2 text-right font-mono text-[#EF4444]">{data.images_failed || 0}</td>
                                <td className="p-2 text-right font-mono">{(data.tokens_prompt || 0).toLocaleString()}</td>
                                <td className="p-2 text-right font-mono">{(data.tokens_completion || 0).toLocaleString()}</td>
                                <td className="p-2 font-mono text-[#A0A0A0]">{data.model || '-'}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="text-[#A0A0A0] text-sm">No usage data available for this Fray.</p>
            )}
          </div>
        )}

        {tab === 'provenance' && (
          <div data-testid="provenance-tab">
            <ProvenanceView frayId={frayId} />
          </div>
        )}

        {/* #655 B2: Questions tab */}
        {tab === 'questions' && qaData && (
          <div data-testid="questions-tab" className="space-y-6">
            {(() => {
              const oqs = qaData.open_questions || [];
              const blockers = oqs.filter(q => q.state === 'still_open' && q.is_pre_phase_blocker);
              const openNonBlockers = oqs.filter(q => q.state === 'still_open' && !q.is_pre_phase_blocker);
              const resolved = oqs.filter(q => q.state !== 'still_open');
              const pendingCount = qaData.pending_blockers || blockers.length;

              return (
                <>
                  {pendingCount === 0 && fray?.status === 'paused' && (
                    <div className="flex items-center justify-between p-4 bg-[#FF4D00]/5 border border-[#FF4D00]/30" data-testid="resume-panel">
                      <div>
                        <div className="text-xs font-mono text-[#FF4D00]">ALL BLOCKERS ANSWERED</div>
                        <div className="text-sm text-[#F5F5F5] mt-1">Resume the debate to continue to the next phase.</div>
                      </div>
                      <button onClick={handleResumeFray} disabled={resuming}
                        className="px-4 py-2 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                        data-testid="resume-btn">
                        <Play className="w-4 h-4" /> {resuming ? 'Resuming…' : 'Resume debate'}
                      </button>
                    </div>
                  )}

                  {blockers.length > 0 && (
                    <section data-testid="blockers-section" aria-live="polite">
                      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#EF4444]/50">
                        <ShieldAlert className="w-4 h-4 text-[#EF4444]" />
                        <h2 className="text-sm font-mono text-[#EF4444] uppercase">Blockers — answer to continue</h2>
                      </div>
                      <div className="space-y-4 mt-4">
                        {blockers.map(oq => (
                          <OQForm
                            key={oq.id}
                            oq={oq}
                            qaAnswers={qaAnswers}
                            setQaAnswers={setQaAnswers}
                            onSubmit={() => handleAnswerOQ(oq)}
                            submitting={qaSubmitting === oq.id}
                            accentColor="#EF4444"
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {openNonBlockers.length > 0 && (
                    <section data-testid="open-section">
                      <div className="flex items-center gap-2 pb-2 border-b border-[#2A2A2A]">
                        <h2 className="text-sm font-mono text-[#F5F5F5] uppercase">Open questions — optional</h2>
                      </div>
                      <div className="space-y-4 mt-4">
                        {openNonBlockers.map(oq => (
                          <OQForm
                            key={oq.id}
                            oq={oq}
                            qaAnswers={qaAnswers}
                            setQaAnswers={setQaAnswers}
                            onSubmit={() => handleAnswerOQ(oq)}
                            submitting={qaSubmitting === oq.id}
                            accentColor="#FF4D00"
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {resolved.length > 0 && (
                    <section data-testid="resolved-section">
                      <div className="flex items-center gap-2 pb-2 border-b border-[#2A2A2A]">
                        <h2 className="text-sm font-mono text-[#A0A0A0] uppercase">Resolved questions</h2>
                      </div>
                      <div className="space-y-3 mt-4 opacity-80">
                        {resolved.map(oq => (
                          <div key={oq.id} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={`resolved-oq-${oq.oq_number}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <span className="text-xs font-mono text-[#A0A0A0]">Q{oq.oq_number}</span>
                                <div className="text-sm text-[#F5F5F5] mt-1">{oq.question}</div>
                              </div>
                              <span className="text-xs font-mono text-[#22C55E] whitespace-nowrap">{oq.state.replace(/_/g, ' ')}</span>
                            </div>
                            {oq.disclosures?.[0]?.content && (
                              <div className="mt-2 pl-3 border-l-2 border-[#2A2A2A] text-xs text-[#A0A0A0] whitespace-pre-wrap">
                                {oq.disclosures[0].content}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* #655 B2: Decisions tab */}
        {tab === 'decisions' && decisionsData && (
          <div data-testid="decisions-tab" className="space-y-6">
            {decisionsData.playbook_root_fray_id && decisionsData.playbook_root_fray_id !== frayId && (
              <Link
                to={`/fray/${decisionsData.playbook_root_fray_id}#decisions`}
                className="text-xs font-mono text-[#FF4D00] hover:underline"
                data-testid="project-decisions-link"
              >
                → View all project decisions
              </Link>
            )}
            {(() => {
              const decisions = decisionsData.decisions || [];
              // Group by raised_in_phase
              const byPhase = decisions.reduce((acc, d) => {
                const p = d.raised_in_phase ?? 0;
                acc[p] = acc[p] || [];
                acc[p].push(d);
                return acc;
              }, {});
              const phases = Object.keys(byPhase).sort((a, b) => Number(a) - Number(b));
              // #1031: index phase summaries by phase_index for header rendering
              const summariesByPhase = (phaseSummariesData?.phase_summaries || [])
                .reduce((acc, s) => { acc[s.phase_index] = s; return acc; }, {});
              return phases.map(p => {
                const summary = summariesByPhase[Number(p)];
                return (
                <section key={p} data-testid={`decisions-phase-${p}`}>
                  <h2 className="text-sm font-mono text-[#A0A0A0] uppercase pb-2 border-b border-[#2A2A2A]">
                    Phase {Number(p) + 1}
                    {summary?.phase_name ? ` — ${summary.phase_name}` : ''}
                  </h2>
                  {summary && (
                    <div
                      data-testid={`phase-summary-${p}`}
                      className="mt-3 p-3 bg-[#141414] border border-[#1F1F1F] text-[11px] font-mono"
                    >
                      {summary.consensus_pct !== null && summary.consensus_pct !== undefined && (
                        <div className="flex items-center gap-3">
                          <span className="text-[#A0A0A0]">
                            CONSENSUS {(summary.consensus_pct * 100).toFixed(0)}%
                          </span>
                          <span
                            className="px-2 py-0.5"
                            style={{
                              color: summary.consensus_reached ? '#F2A900' : '#A0A0A0',
                              borderColor: summary.consensus_reached ? '#F2A900' : '#2A2A2A',
                              borderWidth: 1,
                            }}
                          >
                            {summary.consensus_reached ? 'REACHED' : 'NOT REACHED'}
                          </span>
                          <span className="text-[#A0A0A0]">
                            {summary.rounds_run} ROUND{summary.rounds_run === 1 ? '' : 'S'}
                          </span>
                        </div>
                      )}
                      {summary.narrative && (
                        <div className="mt-2 text-[12px] text-[#F5F5F5] font-sans whitespace-pre-wrap">
                          {summary.narrative}
                        </div>
                      )}
                      {summary.key_dissents &&
                        summary.key_dissents.trim().toLowerCase() !== 'all voices converged' && (
                        <div className="mt-2 text-[11px] text-[#A0A0A0] whitespace-pre-wrap">
                          <span className="text-[#EF4444]">Dissents:</span> {summary.key_dissents}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-3 mt-4">
                    {byPhase[p].map(d => {
                      const badgeColor = d.state === 'ratified' ? '#F2A900' : d.state === 'superseded' ? '#EF4444' : '#A0A0A0';
                      return (
                        <div key={d.id} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={`decision-${d.decision_number}`}>
                          <div className="flex items-start gap-3">
                            <span className="text-xs font-mono text-[#A0A0A0] whitespace-nowrap mt-1">D{d.decision_number}</span>
                            <span
                              className="text-xs font-mono px-2 py-0.5 whitespace-nowrap mt-1"
                              style={{ color: badgeColor, borderColor: badgeColor, borderWidth: 1 }}
                            >
                              {d.state.toUpperCase()}
                            </span>
                            <div className="flex-1">
                              <div className={`text-sm font-medium text-[#F5F5F5] ${d.state === 'superseded' ? 'line-through' : ''}`}>{d.title}</div>
                              <div className="text-xs text-[#A0A0A0] mt-1 whitespace-pre-wrap">{d.description}</div>
                              {d.decided_in_phase !== d.raised_in_phase && (
                                <div className="text-[11px] font-mono text-[#A0A0A0] mt-2">
                                  decided in phase {Number(d.decided_in_phase) + 1}
                                </div>
                              )}
                              {d.superseded_by && (
                                <div className="text-[11px] font-mono text-[#EF4444] mt-2">
                                  superseded by D{decisions.find(x => x.id === d.superseded_by)?.decision_number ?? '?'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
                );
              });
            })()}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 md:gap-3 mt-8 pt-6 border-t border-[#2A2A2A]">
          {fray?.user_id === user?.id ? (
            <button onClick={handleRerun}
              className="px-4 py-2 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium flex items-center gap-2" data-testid="rerun-btn">
              <Zap className="w-4 h-4" /> Re-run Fray
            </button>
          ) : (
            <button onClick={handleFork}
              className="px-4 py-2 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium flex items-center gap-2" data-testid="fork-btn">
              <GitFork className="w-4 h-4" /> Fork & Modify
            </button>
          )}
          {fray?.user_id === user?.id && (
            <button onClick={handleFork}
              className="px-4 py-2 min-h-[44px] border border-[#A855F7]/30 text-sm text-[#A855F7] flex items-center gap-2 hover:bg-[#A855F7]/5" data-testid="fork-own-btn">
              <GitFork className="w-4 h-4" /> Fork as New
            </button>
          )}
          <Link to={`/replay/${frayId}`}
            className="px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5] flex items-center gap-2" data-testid="replay-btn">
            <Play className="w-4 h-4" /> Replay
          </Link>
          <button onClick={handleShare}
            className="px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5] flex items-center gap-2" data-testid="share-btn">
            <Share2 className="w-4 h-4" /> {copied ? 'Link Copied' : 'Share'}
          </button>
          <button onClick={handleExportMarkdown}
            className="px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5] flex items-center gap-2" data-testid="export-md-btn">
            <Download className="w-4 h-4" /> Markdown
          </button>
          <button onClick={handleExportPDF}
            className="px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5] flex items-center gap-2" data-testid="export-pdf-btn">
            <Download className="w-4 h-4" /> PDF
          </button>
          <button onClick={() => setShowSaveToGitHub(true)}
            className="px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5] flex items-center gap-2" data-testid="save-to-github-btn">
            <Github className="w-4 h-4" /> Save to GitHub
          </button>
          {fray?.user_id === user?.id && (
            <button onClick={handleTogglePublic}
              className={`px-4 py-2 border text-sm flex items-center gap-2 transition-colors ${
                fray?.visibility_scope === 'public'
                  ? 'border-[#22C55E]/30 text-[#22C55E]'
                  : 'border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F5F5]'
              }`} data-testid="toggle-public-btn">
              {fray?.visibility_scope === 'public' ? <><Globe className="w-4 h-4" /> Public</> : <><EyeOff className="w-4 h-4" /> Make Public</>}
            </button>
          )}
          {isCatFight && fray?.user_id === user?.id && (
            <button onClick={async () => {
              try {
                const newState = !fray?.is_public;
                await API.put(`/api/frays/${frayId}/publish`, { is_public: newState });
                setFray(prev => ({ ...prev, is_public: newState, published_at: newState ? new Date().toISOString() : prev.published_at }));
              } catch (err) { console.error('Publish failed:', err); }
            }}
              className={`px-4 py-2 border text-sm flex items-center gap-2 transition-colors ${
                fray?.is_public ? 'border-[#EF4444]/30 text-[#EF4444] bg-[#EF4444]/5' : 'border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10'
              }`}>
              <Trophy className="w-4 h-4" /> {fray?.is_public ? 'Published to Gallery' : 'Publish to Gallery'}
            </button>
          )}
        </div>

        {/* Lineage / Version History */}
        {lineage && lineage.total_versions > 1 && (
          <div className="mt-6 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="lineage-section">
            <h3 className="text-xs font-mono text-[#A0A0A0] mb-3 flex items-center gap-2">
              <GitBranch className="w-3.5 h-3.5" /> VERSION HISTORY ({lineage.total_versions})
            </h3>
            <div className="space-y-1.5">
              {lineage.parents.map((p, i) => (
                <Link key={p.id} to={`/fray/${p.id}/results`}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-[#2A2A2A]/30 transition-colors text-xs" data-testid={`lineage-parent-${i}`}>
                  <div className="w-px h-4 bg-[#2A2A2A]" />
                  <span className="text-[#A0A0A0] font-mono capitalize">{p.lineage_type || 'original'}</span>
                  <span className="text-[#F5F5F5]">{p.owner_name}</span>
                  {p.verdict && <span className="font-mono" style={{ color: VERDICT_COLORS[p.verdict] }}>{p.verdict}</span>}
                  <span className="text-[#A0A0A0] ml-auto">{new Date(p.created_at).toLocaleDateString()}</span>
                </Link>
              ))}
              {/* Current */}
              <div className="flex items-center gap-3 px-3 py-2 bg-[#FF4D00]/5 border border-[#FF4D00]/20 text-xs">
                <Zap className="w-3 h-3 text-[#FF4D00]" />
                <span className="text-[#FF4D00] font-mono capitalize">{lineage.current.lineage_type || 'current'}</span>
                <span className="text-[#F5F5F5] font-medium">{lineage.current.owner_name}</span>
                {lineage.current.forked_from_name && <span className="text-[#A0A0A0]">forked from {lineage.current.forked_from_name}</span>}
                {lineage.current.verdict && <span className="font-mono" style={{ color: VERDICT_COLORS[lineage.current.verdict] }}>{lineage.current.verdict}</span>}
                <span className="text-[#A0A0A0] ml-auto">current</span>
              </div>
              {lineage.children.map((c, i) => (
                <Link key={c.id} to={`/fray/${c.id}/results`}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-[#2A2A2A]/30 transition-colors text-xs" data-testid={`lineage-child-${i}`}>
                  <div className="w-px h-4 bg-[#2A2A2A]" />
                  <span className="text-[#A855F7] font-mono capitalize">{c.lineage_type || 'fork'}</span>
                  <span className="text-[#F5F5F5]">{c.owner_name}</span>
                  {c.verdict && <span className="font-mono" style={{ color: VERDICT_COLORS[c.verdict] }}>{c.verdict}</span>}
                  <span className="text-[#A0A0A0] ml-auto">{new Date(c.created_at).toLocaleDateString()}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Fork attribution */}
        {fray?.forked_from_name && (
          <div className="mt-4 text-xs text-[#A0A0A0] font-mono flex items-center gap-2">
            <GitFork className="w-3 h-3 text-[#A855F7]" />
            Forked from {fray.forked_from_name}'s Fray
            {fray.parent_fray_id && (
              <Link to={`/fray/${fray.parent_fray_id}/results`} className="text-[#FF4D00] hover:underline">View original</Link>
            )}
          </div>
        )}

        {/* #1084: Project versions — all frays sharing playbook_root_fray_id.
            Renders v1, v2, v3, ... chronologically for users iterating on
            a project (e.g. Mira's PSA pitch deck reviews). Hidden for solo
            frays (no project root yet). */}
        <ProjectVersionsView
          lineage={lineage}
          onSelectFray={(id) => navigate(`/fray/${id}/results`)}
        />

        <SaveToGitHub
          open={showSaveToGitHub}
          onClose={() => setShowSaveToGitHub(false)}
          fray={fray}
          frayId={frayId}
        />
      </div>
    </div>
  );
}


function StoppedFrayScreen({ fray, frayId, onRerun }) {
  const navigate = useNavigate();
  const [voices, setVoices] = useState(fray.voices || []);
  const [availableModels, setAvailableModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openPicker, setOpenPicker] = useState(null);
  const [modelSearch, setModelSearch] = useState('');
  const [debateMode, setDebateMode] = useState(fray.debate_mode || 'panel');
  const [frictionLevel, setFrictionLevel] = useState(fray.friction_level || 3);
  const [debateModes, setDebateModes] = useState([]);
  const [frictionLabels, setFrictionLabels] = useState([]);

  useEffect(() => {
    API.get('/api/byok/models').then(r => setAvailableModels(r.data.models || [])).catch(() => {});
    API.get('/api/debate-modes').then(r => setDebateModes(r.data.modes || [])).catch(() => {});
    API.get('/api/friction-prompts').then(r => setFrictionLabels(r.data.levels || [])).catch(() => {});
  }, []);

  const updateVoiceModel = async (voiceIdx, modelId, tier) => {
    const updated = voices.map((v, i) => i === voiceIdx ? { ...v, default_model: modelId, model_tier: tier || 'economy' } : v);
    setVoices(updated);
    setOpenPicker(null);
    setModelSearch('');
  };

  const saveAndRerun = async () => {
    setSaving(true);
    try {
      // Save voice model changes
      await API.put(`/api/frays/${frayId}/voices`, {
        voices: voices.map(v => ({ name: v.name, model: v.default_model, model_tier: v.model_tier }))
      });
      // Save debate mode and friction level
      await API.put(`/api/frays/${frayId}/debate-config`, {
        debate_mode: debateMode,
        friction_level: frictionLevel,
      });
      // Create re-run with the updated config
      const res = await API.post(`/api/frays/${frayId}/rerun`);
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      console.error('Save/rerun failed:', err);
      setSaving(false);
    }
  };

  const setAllToModel = (modelId, tier) => {
    setVoices(prev => prev.map(v => ({ ...v, default_model: modelId, model_tier: tier || 'economy' })));
    setSaved(false);
  };

  const filteredModels = availableModels.filter(m =>
    !modelSearch || m.id.toLowerCase().includes(modelSearch.toLowerCase()) || m.name?.toLowerCase().includes(modelSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-3xl mx-auto">
        <Link to="/dashboard" className="flex items-center gap-1 text-xs text-[#A0A0A0] hover:text-[#FF4D00] font-mono mb-6 min-h-[44px]">
          <ArrowLeft className="w-3 h-3" /> Dashboard
        </Link>

        <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-[#A0A0A0] shrink-0 mt-1" />
            <div>
              <h1 className="text-xl font-bold font-['Barlow_Condensed']">
                {fray.status === 'stopped' ? 'Fray Stopped' : fray.status === 'failed' ? 'Fray Failed' : fray.status === 'interrupted' ? 'Fray Interrupted' : 'Ready to Run'}
              </h1>
              <p className="text-xs text-[#A0A0A0] mt-1">{fray.template_name}</p>
            </div>
          </div>
          <p className="text-sm text-[#A0A0A0] mb-4">{fray.scenario?.slice(0, 200)}{fray.scenario?.length > 200 ? '...' : ''}</p>
        </div>

        {/* Voice Model Editor */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold font-['Barlow_Condensed'] flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-[#FF4D00]" /> Edit Voice Models
            </h2>
            <button onClick={() => setAllToModel('google/gemini-2.5-flash', 'standard')}
              className="text-[9px] font-mono px-2 py-1 border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#FF4D00] hover:border-[#FF4D00]/40">
              Set all to Gemini 2.5 Flash
            </button>
          </div>

          <div className="space-y-2">
            {voices.map((v, i) => (
              <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={`edit-voice-${i}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-7 h-7 flex items-center justify-center border-2 text-xs font-bold font-['Barlow_Condensed']"
                    style={{ borderColor: v.color, color: v.color }}>{v.name.charAt(0)}</div>
                  <span className="text-sm font-medium font-['Barlow_Condensed']" style={{ color: v.color }}>{v.name}</span>
                </div>

                <div className="relative">
                  <button onClick={() => { setOpenPicker(openPicker === i ? null : i); setModelSearch(''); }}
                    className={`w-full flex items-center justify-between px-3 py-2 border text-left transition-colors ${
                      openPicker === i ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#FF4D00]/40'
                    }`} data-testid={`model-select-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[9px] px-1.5 py-0.5 border font-mono shrink-0"
                        style={{ color: TIER_COLORS[v.model_tier] || '#A0A0A0', borderColor: (TIER_COLORS[v.model_tier] || '#A0A0A0') + '40' }}>
                        {v.model_tier?.toUpperCase()}
                      </span>
                      <span className="text-xs font-mono text-[#F5F5F5] truncate">{v.default_model}</span>
                    </div>
                    <ChevronDown className={`w-3.5 h-3.5 text-[#A0A0A0] transition-transform ${openPicker === i ? 'rotate-180' : ''}`} />
                  </button>

                  {openPicker === i && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#2A2A2A] shadow-xl max-h-56 flex flex-col">
                      <div className="p-2 border-b border-[#2A2A2A]">
                        <div className="relative">
                          <Search className="w-3 h-3 text-[#A0A0A0] absolute left-2.5 top-1/2 -translate-y-1/2" />
                          <input type="text" value={modelSearch} onChange={e => setModelSearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs focus:outline-none focus:border-[#FF4D00]"
                            placeholder="Search models..." autoFocus />
                        </div>
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {filteredModels.slice(0, 30).map(m => (
                          <button key={m.id} onClick={() => updateVoiceModel(i, m.id, m.tier)}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-left border-b border-[#0F0F0F] ${
                              v.default_model === m.id ? 'bg-[#FF4D00]/10' : 'hover:bg-[#2A2A2A]/50'
                            }`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-[8px] w-10 text-center px-1 py-0.5 border font-mono shrink-0"
                                style={{ color: TIER_COLORS[m.tier], borderColor: TIER_COLORS[m.tier] + '40' }}>
                                {m.tier?.toUpperCase()?.slice(0, 4)}
                              </span>
                              <span className="text-xs text-[#F5F5F5] truncate">{m.name || m.id}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Debate Mode & Friction Level */}
        <div className="mb-6 space-y-4">
          <h2 className="text-lg font-semibold font-['Barlow_Condensed'] flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#FF4D00]" /> Debate Settings
          </h2>

          {/* Debate Mode Selector */}
          <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
            <label className="text-xs font-mono text-[#A0A0A0] mb-2 block">DEBATE MODE</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {debateModes.map(m => (
                <button key={m.id} onClick={() => m.available && setDebateMode(m.id)}
                  disabled={!m.available}
                  className={`p-3 border text-left transition-colors ${
                    debateMode === m.id
                      ? 'border-[#FF4D00] bg-[#FF4D00]/10'
                      : m.available
                        ? 'border-[#2A2A2A] hover:border-[#FF4D00]/40'
                        : 'border-[#2A2A2A] opacity-40 cursor-not-allowed'
                  }`}
                  data-testid={`debate-mode-${m.id}`}>
                  <span className="text-sm font-medium block">{m.name}</span>
                  <span className="text-[10px] text-[#A0A0A0] block mt-0.5">{m.description?.slice(0, 60)}</span>
                  {!m.available && <span className="text-[8px] font-mono text-[#FF4D00] mt-1 block">{m.min_tier}+ plan</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Friction Level Slider */}
          <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
            <label className="text-xs font-mono text-[#A0A0A0] mb-2 block">FRICTION LEVEL</label>
            <div className="flex items-center gap-4">
              <input type="range" min="1" max="10" value={frictionLevel}
                onChange={e => setFrictionLevel(parseInt(e.target.value))}
                className="flex-1 accent-[#FF4D00] h-2 cursor-pointer"
                data-testid="friction-slider" />
              <div className="text-right shrink-0 w-28">
                <span className="text-lg font-bold text-[#FF4D00] font-mono">{frictionLevel}</span>
                <span className="text-[10px] text-[#A0A0A0] block">
                  {frictionLabels.find(l => l.level === frictionLevel)?.label || ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={saveAndRerun} disabled={saving}
            className="px-8 py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold flex items-center justify-center gap-2 hover:bg-[#FF4D00]/90 disabled:opacity-50"
            data-testid="save-rerun-btn">
            {saving ? <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Launching...</> :
              <><Zap className="w-4 h-4" /> Save Models & Re-run</>}
          </button>
          <Link to="/dashboard" className="px-6 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0] flex items-center justify-center">Dashboard</Link>
        </div>
      </div>
    </div>
  );
}
